import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  BellRing,
  CheckCheck,
  ExternalLink,
  Mail,
  MessageSquarePlus,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api, type Alert } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAlertsStream } from "@/hooks/useAlertsStream";
import { useAuth } from "@/hooks/useAuth";
import { usePlan } from "@/hooks/usePlan";
import { LoginRequired } from "@/components/LoginRequired";
import { openUpgrade } from "@/components/UpgradeDialog";
import { Crown } from "lucide-react";
import { fromNowFR, formatNumber } from "@/lib/utils";
import { cn } from "@/lib/utils";

// ───────────────────────── alert kind metadata ─────────────────────────
// Maps the alert_kind enum (new_thread | new_post | thread_revived) to its
// French label, icon, and an accent class. All accents stay inside our
// semantic colour palette to avoid harsh grey-on-black contrasts.

type AlertKind = "new_thread" | "new_post" | "thread_revived";

// Tokens : `text-success` / `text-info` / `text-warning` resolve through CSS
// vars so colours auto-adapt to light & dark themes — no hardcoded hue.
const KIND_META: Record<
  AlertKind,
  { label: string; icon: React.ComponentType<{ className?: string }>; tone: string; accent: string }
> = {
  new_thread: {
    label: "Nouveau sujet",
    icon: MessageSquarePlus,
    tone: "text-success",
    accent: "bg-success/15 ring-1 ring-success/30",
  },
  new_post: {
    label: "Nouvelle réponse",
    icon: Sparkles,
    tone: "text-info",
    accent: "bg-info/15 ring-1 ring-info/30",
  },
  thread_revived: {
    label: "Sujet relancé",
    icon: TrendingUp,
    tone: "text-warning",
    accent: "bg-warning/15 ring-1 ring-warning/30",
  },
};

function kindMeta(kind: string) {
  return KIND_META[(kind as AlertKind) in KIND_META ? (kind as AlertKind) : "new_post"];
}

// ─────────────────────────── page ───────────────────────────

export default function AlertsPage() {
  useSeo({ title: "Mes alertes", canonicalPath: "/alerts", noindex: true });
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show a friendly placeholder while we don't know yet. Sidebar stays
  // navigable thanks to the layout outlet, so this empty state is safe.
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Header live={false} unread={0} />
        <div className="text-sm text-foreground-muted">Chargement…</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="flex flex-col gap-6">
        <Header live={false} unread={0} />
        <LoginRequired
          icon={Bell}
          title="Connectez-vous pour voir vos alertes"
          description={
            <>
              Les alertes vous préviennent en temps réel des nouveautés sur les
              forums, sujets et utilisateurs que vous suivez. Connectez-vous (par
              code email, sans mot de passe) pour activer vos notifications.
            </>
          }
          bullets={[
            "Suivez un forum pour être notifié des nouveaux sujets et des sujets relancés.",
            "Suivez un utilisateur pour être notifié à chaque message qu'il publie.",
            "Le flux est en temps réel — pas besoin de rafraîchir la page.",
          ]}
        />
      </div>
    );
  }

  return <AlertsContent appUserId={user.id} />;
}

// ───────────────────── authenticated view ─────────────────────

function AlertsContent({ appUserId }: { appUserId: number }) {
  const qc = useQueryClient();
  const { data: alerts = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["alerts", appUserId],
    queryFn: () => api.alerts(),
  });
  const lastLive = useAlertsStream(appUserId);

  // Invalidate on SSE pings — the Go API publishes to redis on new alerts.
  useEffect(() => {
    if (lastLive) qc.invalidateQueries({ queryKey: ["alerts", appUserId] });
  }, [lastLive, qc, appUserId]);

  const mark = useMutation({
    mutationFn: (id: number) => api.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", appUserId] }),
  });

  // Single bulk endpoint — no more N unitary POSTs.
  const markAll = useMutation({
    mutationFn: () => api.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts", appUserId] });
      toast.success("Toutes les alertes ont été marquées lues.");
    },
    onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : "?"}`),
  });

  const unread = useMemo(() => alerts.filter((a) => !a.read_at).length, [alerts]);

  return (
    <div className="flex flex-col gap-6">
      <Header live={!!lastLive} unread={unread}>
        {unread > 0 && (
          <Button size="sm" variant="outline" onClick={() => markAll.mutate()}>
            <CheckCheck className="size-4" />
            Tout marquer lu
          </Button>
        )}
      </Header>

      <RealtimeUpsell />

      <DigestToggle />

      {isLoading ? (
        <div className="text-sm text-foreground-muted">Chargement des alertes…</div>
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : alerts.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="flex flex-col gap-2">
          {alerts.map((a) => (
            <AlertRow key={a.id} alert={a} onMarkRead={(id) => mark.mutate(id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ───────────────────────── sub-components ─────────────────────────

function Header({
  live,
  unread,
  children,
}: {
  live: boolean;
  unread: number;
  children?: React.ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-center gap-3">
      <h1 className="text-2xl font-semibold text-foreground">Alertes</h1>

      {live && (
        <Badge variant="default" className="bg-success/15 text-success ring-1 ring-success/30">
          <BellRing className="size-3 mr-1" />
          Temps réel
        </Badge>
      )}

      {unread > 0 && (
        <Badge variant="destructive" className="ring-1 ring-destructive/30">
          <Bell className="size-3 mr-1" />
          {formatNumber(unread)} non lue{unread > 1 ? "s" : ""}
        </Badge>
      )}

      <div className="ml-auto flex gap-2">{children}</div>
    </header>
  );
}

// Realtime-alerts upsell. Free users get the daily email digest but not the
// instant in-app alerts ; this banner makes that gap visible and sells Pro.
// Renders nothing for Pro members.
function RealtimeUpsell() {
  const { isFree, isLoading } = usePlan();
  if (isLoading || !isFree) return null;
  return (
    <Card className="border-primary/30">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-start gap-3">
          <div className="size-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <BellRing className="size-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              Alertes en temps réel
              <Badge className="ml-2 align-middle">Pro</Badge>
            </p>
            <p className="text-xs text-foreground-muted max-w-md">
              En gratuit, vous recevez le récap quotidien par email. Passez à Pro pour
              être prévenu à la seconde, dans l'app, dès qu'un de vos suivis bouge.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() =>
            openUpgrade({
              reason:
                "Les alertes en temps réel sont réservées aux membres Boursicote Pro.",
            })
          }
        >
          <Crown className="size-4" />
          Passer à Pro
        </Button>
      </CardContent>
    </Card>
  );
}

// Email digest opt-in/out. The daily digest is a curated summary (forum buzz
// signals, per-user post counts, per-thread reply counts) — not a replay of
// every post — so it stays free of troll noise.
function DigestToggle() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.me(),
  });
  const enabled = data?.user?.digest_enabled ?? true;

  const toggle = useMutation({
    mutationFn: (next: boolean) => api.updatePreferences({ digest_enabled: next }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["me"] });
      toast.success(
        res.digest_enabled
          ? "Récap email quotidien activé."
          : "Récap email quotidien désactivé."
      );
    },
    onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : "?"}`),
  });

  return (
    <Card>
      <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="size-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Mail className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Récap email quotidien</p>
            <p className="text-xs text-foreground-muted max-w-md">
              Un résumé du matin de l'activité sur vos suivis (forums qui
              s'agitent, utilisateurs et fils suivis). Agrégé, sans spam.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant={enabled ? "default" : "outline"}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(!enabled)}
          className="shrink-0 self-start sm:self-auto"
        >
          {enabled ? "Activé" : "Désactivé"}
        </Button>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card className="p-8 text-center">
      <CardContent className="flex flex-col items-center gap-3 p-0">
        <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Bell className="size-6" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">Aucune alerte pour l'instant</h2>
        <p className="text-sm text-foreground-muted max-w-md">
          Suivez des forums ou des utilisateurs depuis leurs pages respectives :
          vous recevrez ici une notification dès qu'un nouveau sujet, une
          réponse ou une réactivation se produit.
        </p>
        <div className="flex gap-2 mt-2">
          <Button asChild size="sm">
            <Link to="/forums">Voir les forums</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/users">Voir les utilisateurs</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="p-8 text-center">
      <CardContent className="flex flex-col items-center gap-3 p-0">
        <div className="size-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
          <Bell className="size-6" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">Impossible de charger les alertes</h2>
        <p className="text-sm text-foreground-muted max-w-md">
          Une erreur réseau est survenue. Vérifiez votre connexion puis réessayez.
        </p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          Réessayer
        </Button>
      </CardContent>
    </Card>
  );
}

function AlertRow({
  alert,
  onMarkRead,
}: {
  alert: Alert;
  onMarkRead: (id: number) => void;
}) {
  const meta = kindMeta(alert.kind);
  const Icon = meta.icon;
  const isUnread = !alert.read_at;

  // Internal link : prefer the thread page if we have an id, otherwise the
  // forum listing. External link to Boursorama is always available when we
  // have the thread URL stored.
  const internalHref = alert.thread_id
    ? `/threads/${alert.thread_id}`
    : alert.forum_id
      ? `/forums/${alert.forum_id}/threads`
      : null;

  return (
    <li
      className={cn(
        "rounded-lg border border-border bg-surface text-foreground transition-colors",
        "hover:bg-surface-muted",
        isUnread && "ring-1 ring-primary/30"
      )}
    >
      <div className="flex items-start gap-3 p-3">
        {/* Kind icon */}
        <div className={cn("size-9 rounded-md flex items-center justify-center shrink-0", meta.accent)}>
          <Icon className={cn("size-4", meta.tone)} />
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className={cn("text-xs font-medium", meta.tone)}>{meta.label}</span>
            {alert.forum_title && (
              <Link
                to={`/forums/${alert.forum_id}/threads`}
                className="text-xs text-foreground-muted hover:text-foreground hover:underline"
                title="Voir le forum"
              >
                {alert.forum_title}
              </Link>
            )}
            <span className="text-xs text-foreground-muted">·</span>
            <span className="text-xs text-foreground-muted" title={alert.created_at}>
              {fromNowFR(alert.created_at)}
            </span>
            {alert.orphan && (
              <Badge variant="outline" className="text-foreground-muted text-[10px] py-0">
                suivi supprimé
              </Badge>
            )}
          </div>

          {alert.thread_title ? (
            internalHref ? (
              <Link
                to={internalHref}
                className="text-sm font-medium text-foreground hover:text-primary truncate"
              >
                {alert.thread_title}
              </Link>
            ) : (
              <span className="text-sm font-medium text-foreground truncate">{alert.thread_title}</span>
            )
          ) : (
            <span className="text-sm text-foreground-muted italic">
              Sujet n°{alert.thread_id ?? "?"} (titre indisponible)
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {alert.thread_url && (
            <Button
              asChild
              size="icon"
              variant="ghost"
              title="Ouvrir sur Boursorama"
              className="text-foreground-muted hover:text-foreground"
            >
              <a href={alert.thread_url} target="_blank" rel="noreferrer noopener">
                <ExternalLink className="size-4" />
              </a>
            </Button>
          )}
          {isUnread && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMarkRead(alert.id)}
              title="Marquer comme lu"
            >
              <CheckCheck className="size-3.5" />
              Lu
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
